import { ADVISORS_BASE } from '@src/components/sco/Advisors/constants'
import { normalizeAsPath } from '@src/components/sco/Advisors/utils'
import { isAdvisorProfileHubSegment } from '@src/components/sco/Advisors/utils/advisorProfileHub'

/**
 * `/advisors/**` routes a guest may open inside `AdvisorsLayout` (L3). Every other Advisors route
 * shows guests the V2 landing. `/members/[slug]` (+ `/book`) and the become-an-advisor pages sit
 * outside `AdvisorsLayout` and are guest-reachable on their own.
 *
 * - `landing`   `/advisors`
 * - `browse`    `/advisors/browse`
 * - `directory` `/advisors/directory`, `/advisors/directory/[categoryKey]`
 * - `profile`   `/advisors/profile/[memberSlug]` (not the own-profile hub: `/profile`, `/profile/edit`, …)
 * - `book`      `/advisors/sessions/new` (the shared booking form; it bounces to the directory without
 *               `?expertUserId=`)
 */
export type AdvisorsGuestRoute = 'landing' | 'browse' | 'directory' | 'profile' | 'book'

/** Standalone `/advisors` only — Request Workspace embeds are never guest-reachable. */
export const advisorsGuestRoute = (asPath: string): AdvisorsGuestRoute | null => {
  const path = normalizeAsPath(asPath || '').replace(/\/+$/, '') || '/'
  if (path === ADVISORS_BASE) {
    return 'landing'
  }
  if (!path.startsWith(`${ADVISORS_BASE}/`)) {
    return null
  }
  const segments = path.slice(ADVISORS_BASE.length + 1).split('/')

  if (segments.length === 1 && segments[0] === 'browse') {
    return 'browse'
  }
  if (segments[0] === 'directory' && segments.length <= 2 && segments.every(Boolean)) {
    return 'directory'
  }
  if (
    segments.length === 2 &&
    segments[0] === 'profile' &&
    !!segments[1] &&
    !isAdvisorProfileHubSegment(segments[1])
  ) {
    return 'profile'
  }
  if (segments.length === 2 && segments[0] === 'sessions' && segments[1] === 'new') {
    return 'book'
  }
  return null
}

/**
 * Guest routes that server-render (SEO). The landing is guest-only — members get the product home.
 * `book` stays client-only: it is `noindex`, and the booking form keeps its draft in `sessionStorage`.
 */
export const advisorsRouteServerRenders = (route: AdvisorsGuestRoute | null, isLoggedOut: boolean): boolean =>
  route === 'browse' || route === 'directory' || route === 'profile' || (route === 'landing' && isLoggedOut)
